import { useState, useEffect } from "react";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, PDF_STATUS, COLORS } from "@/common/constants";
import Pagination from "@/components/Layouts/Pagination";
import { Box, Button, SkeletonText, Table, TableContainer, Tbody, Td, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { getListInspectionResults, InspectionResult } from "@/services/inspectionResultService";
import { format } from "date-fns";
import { FaCloudUploadAlt } from "react-icons/fa";
import FileIcon from "@/assets/FileIcon";
import { downloadFile } from "@/services/downloadService";
import { useNavigate } from "react-router-dom";
import InProgress from "@/assets/InProgress";
import CompletedIcon from "@/assets/CompletedIcon";
import NewIcon from "@/assets/NewIcon";

import '../styles/ListPage.css';

const ListInspectionResults = () => {
    const toast = useToast();
    const navigate = useNavigate();

    const COLUMNS = ['Date', 'Report Name', 'Vessel Name', 'IMO', 'Report Type', 'Status', 'File'];
    const NUM_COLUMNS = COLUMNS.length;

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPageNumber, setTotalPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [dataList, setDataList] = useState<InspectionResult[]>([]);

    const showToast = (error: {code: string | number, message: string}) => {
        toast({
            title: error?.code,
            description: error?.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const getData = async () => {
        setIsLoading(true)
        const res = await getListInspectionResults(currentPageNumber, currentPageSize);
        if (res.data){
            setDataList(res.data);
            setTotalPageNumber(res.totalPage);
            setHasNextPage(res.hasNextPage);
        } else if (res.status === 200){
            setDataList([]);
            setTotalPageNumber(0);
            setHasNextPage(false);
        } else {
            showToast({code: res.status ?? '', message: res.message ?? 'Could not get data'});
            setDataList([]);
            setTotalPageNumber(0);
            setHasNextPage(false);
        }
        setIsLoading(false)
    };

    const handleDownload = async (idFile: string) => {
			await downloadFile(idFile); // remove _uploadType
    };

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        getData();
    }, [currentPageNumber, currentPageSize]);

    const renderStatus = (status: number) => {
        switch(status) {
            case PDF_STATUS.NEW:
                return <NewIcon />
            case PDF_STATUS.IN_PROGRESS:
                return <InProgress />
            case PDF_STATUS.COMPLETED:
                return <CompletedIcon />
            default:
                return null;
        }
    };

    const renderRows = () => {
        if (isLoading) {
            return (
                <Tr>
                    <Td colSpan={NUM_COLUMNS}>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='3' />
                    </Td>
                </Tr>
            );
        }

        if ((!isLoading && (!dataList || dataList.length === 0))) {
            return (
                <Tr>
                    <Td colSpan={NUM_COLUMNS} textAlign="center">Data not found</Td>
                </Tr>
            );
        }

        return dataList.map((item) => (
            <Tr key={item.id}>
                <Td>{item.created ? format(new Date(item.created), 'dd-MM-yyyy') : '-'}</Td>
                <Td>{item.reportName || '-'}</Td>
                <Td>{item.vesselName || '-'}</Td>
                <Td>{item.imoNumber || '-'}</Td>
                <Td>{item.reportType || '-'}</Td>
                <Td textAlign='center' justifyItems='center'>{renderStatus(item.status)}</Td>
                <Td>{item.fileId ? <FileIcon onClick={() => handleDownload(item.id)} style={{ cursor: 'pointer' }} /> : '-'}</Td>
            </Tr>
        ));
    }

    return (
        <div className="table-container">
            <div className="table-filter-action-container">
                <Button
                    leftIcon={<FaCloudUploadAlt />}
                    className="table-add-button"
                    bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10}
                    onClick={() => {
                        navigate('/inspection-result/upload');
                    }}
                >
                    Upload Inspection Result
                </Button>
            </div>
            <TableContainer bgColor="#fff" borderRadius="13">
            <Box overflowX="auto">
                <Table variant='simple'>
                    <Thead>
                        <Tr>
                            {COLUMNS.map((header) => (
                                <Th key={header} color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign={["Result", "Status", "Action"].includes(header) ? 'center' : 'left'}>
                                    {header}
                                </Th>
                            ))}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {renderRows()}
                    </Tbody>
                </Table>
            </Box>
            <Pagination
                currentPageSize={currentPageSize}
                currentPageNumber={currentPageNumber}
                totalPageNumber={totalPageNumber}
                handleChangePageSize={setCurrentPageSize}
                handleChangePageNumber={setCurrentPageNumber}
                hasNextPage={hasNextPage}
            />
        </TableContainer>
            </div>
    );
};

export default ListInspectionResults;
